// @ts-strict-ignore
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Dictionary } from 'typescript-collections';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { Vector3 } from 'three';
import { ProjectModel } from '../model/project.model';
import { ArticleManager } from '@article/manager/article.manager';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { ArticleInsertProcessorRegistry } from '../components/article/processor/prepare/insert/article-insert-processor-registry';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArrayUtils } from '@shared/util/array-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { ProjectVO } from '../model/vo/project.vo';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ConnectionAreaUsageVO, ConnectionAreaVO, ConnectionVO, MetaElementUsageIdentityVO, MetaElementUsageVO } from '@shared/vo/meta-element.vo';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { IArticleInsertProcessor } from '../components/article/processor/prepare/insert/i-article-insert.processor';
import { CarrierBlockUtils } from '../components/article/util/carrier-block-utils';
import { ArticleLineHolder } from '@article/helper/article-line-holder';
import { PropertyValueVO } from '../components/article/model/vo/property.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleConstants } from '@article/constants/article-constants';
import { OverlayService } from '@shared/overlay/overlay.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { TextureConnectionUtils } from '@3d-scene/processor/texture-connection-utils';
import { Injectable } from '@angular/core';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { CameraControlsService } from '@3d-scene/common/scene/controls/camera-controls.service';
import { ProjectModelService } from '../service/project-model.service';
import { ProjectArticleService } from '../service/project-article.service';
import { SelectionModelService } from '../components/article/service/selection-model.service';
import { MiniTerminalBlocksArticleManager } from '@article/manager/mini-terminal-blocks-article.manager';
import { ProjectMetaElementService } from './project-meta-element.service';
import { DetachArticleUsageService } from '../service/detach-article-usage.service';
import { ClearService } from '../service/clear.service';
import { MiniTerminalBlocksProjectCreationServiceDelegator } from '@configuration/wizard/service/mini-terminal-blocks-project-creation-service.delegator';
import { ProjectUtils } from '../components/project/util/project-utils';
import { ArticleContainerService } from '@3d-scene/service/article-container.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';

class ArticleContainerGroup {
  public groupId: string;
  public articleContainers: ArticleContainer[];
  public forwardConnectedUsageMap: Dictionary<string, ArticleUsageVO>;
}

@Injectable({ providedIn: 'root' })
export class CreateNewProjectWithPresetArticlesManager {
  private static readonly LINE_SEPARATOR: string = '\n';
  private static readonly ERROR_TITLE: string = 'global.title.error';
  private static readonly ERROR_MESSAGE: string = 'global.message.unexpected_error';
  private static readonly CONVERT_RESULT_TITLE: string = 'createNewProjectWithPresetArticlesManager.copy.title.error';
  private static readonly CONVERT_RESULT_MESSAGE: string = 'createNewProjectWithPresetArticlesManager.copy.message.articles_can_not_added';
  private static readonly CONVERT_TITLE: string = 'createNewProjectWithPresetArticlesManager.copy.title.confirm_convert_project';
  private static readonly CONVERT_MESSAGE: string = 'createNewProjectWithPresetArticlesManager.copy.message.confirm_convert_project';

  private static readonly POINT_TO_SORT_BY_X: Vector3 = new Vector3(10000, 0, 0);
  private static readonly ARTICLE_OFFSET: Vector3 = new Vector3(100, 0, 0);

  private _isActive: boolean = false;
  private _targetArticleContainerGroups: ArticleContainerGroup[];
  private _processedArticleLine: AdvancedLine;
  private _insertArticleHelper: InsertArticleHelper;
  private _notTransferredArticleMap: Dictionary<string, number> = new Dictionary<string, number>();

  constructor(
    private projectModel: ProjectModel,
    private projectModelService: ProjectModelService,
    private projectArticleService: ProjectArticleService,
    private projectMetaElementService: ProjectMetaElementService,
    private selectionModelService: SelectionModelService,
    private articleManager: ArticleManager,
    private miniTerminalBlocksArticleManager: MiniTerminalBlocksArticleManager,
    private projectContentPresenter: ProjectContentPresenter,
    private insertProcessorRegistry: ArticleInsertProcessorRegistry,
    private localeResourceManager: LocaleResourceManager,
    private alertHelper: AlertHelper,
    private overlayService: OverlayService,
    private textureConnectionUtils: TextureConnectionUtils,
    private compositeArticleInfoUtils: CompositeArticleInfoUtils,
    private containerService: ContainerService,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private articleLineHolder: ArticleLineHolder,
    private cameraControlsService: CameraControlsService,
    private detachArticleUsageService: DetachArticleUsageService,
    private miniTerminalBlocksProjectCreationServiceDelegator: MiniTerminalBlocksProjectCreationServiceDelegator,
    private clearService: ClearService,
    private articleContainerService: ArticleContainerService,
    private connectionInfoService: ConnectionInfoService
  ) {}

  private clean(): void {
    this._isActive = false;
    this._targetArticleContainerGroups = null;
    this._processedArticleLine = null;
    this._insertArticleHelper = null;
    this._notTransferredArticleMap = new Dictionary<string, number>();
  }

  private handleSuccess(): void {
    const articleNumbers: string[] = this._notTransferredArticleMap.keys();
    if (ArrayUtils.isNotEmpty(articleNumbers)) {
      const title: string = this.localeResourceManager.getValueByKey(CreateNewProjectWithPresetArticlesManager.CONVERT_RESULT_TITLE);
      const message: string = this.createConvertResultMessage(this._notTransferredArticleMap);
      this.alertHelper.showAlert(title, message, {}, [AlertButton.OK], "error");
    }
    this.articleLineHolder.clearArticleLineCache();
    this.clean();
    // The delay is required for the to be sure that all article containers and UI components are 'ready' state.
    setTimeout(() => {
      this.showDefaultView();
      this.closeWaitPopup();
    }, 2000);
  }

  private createConvertResultMessage(articleMap: Dictionary<string, number>): string {
    let articleNumbers: string = '';
    for (const articleNumber of articleMap.keys()) {
      const articleQuantity: number = articleMap.getValue(articleNumber);
      articleNumbers += CreateNewProjectWithPresetArticlesManager.LINE_SEPARATOR + articleNumber + ' (x' + articleQuantity + ')';
    }
    return this.localeResourceManager.getValueByKey(CreateNewProjectWithPresetArticlesManager.CONVERT_RESULT_MESSAGE, [articleNumbers]);
  }

  private async handleError(messageKey: string, resetCurrentProject: boolean = true): Promise<void> {
    const title: string = this.localeResourceManager.getValueByKey(CreateNewProjectWithPresetArticlesManager.ERROR_TITLE);
    const message: string = this.localeResourceManager.getValueByKey(messageKey);
    this.closeWaitPopup();
    this.alertHelper.showAlert(title, message, {}, [AlertButton.OK], "exclamation");
    this.clean();
    if (resetCurrentProject) {
      await this.projectModelService.clearCurrentProject();
      this.showDefaultView();
    }
  }

  private showDefaultView(): void {
    this.cameraControlsService.requestDefaultPosition();
  }

  private openWaitPopup(): void {
    this.overlayService.requestOverlayShow(OverlayBackground.color);
  }

  private closeWaitPopup(): void {
    this.overlayService.requestOverlayHide(OverlayBackground.color);
  }

  public async startNewProjectWithPresetArticlesHandler(): Promise<void> {
    const title: string = this.localeResourceManager.getValueByKey(CreateNewProjectWithPresetArticlesManager.CONVERT_TITLE);
    const message: string = this.localeResourceManager.getValueByKey(CreateNewProjectWithPresetArticlesManager.CONVERT_MESSAGE);
    const answer = await this.alertHelper.showAlert(title, message, {}, [AlertButton.YES, AlertButton.NO], "help");
    if (AlertButton.YES === answer) {
      await this.convert();
    }
  }

  private async convert(): Promise<void> {
    this.clean();
    this.openWaitPopup();
    this._isActive = true;
    this._targetArticleContainerGroups = this.findTargetArticleContainers();
    if (ArrayUtils.isEmpty(this._targetArticleContainerGroups)) {
      await this.handleError(CreateNewProjectWithPresetArticlesManager.ERROR_MESSAGE, false);
      return;
    }
    const baseRangeOnLineContainer: ArticleContainer = this.getBaseRangeOnLineContainer(this._targetArticleContainerGroups);
    await this.detachTargetArticleContainers(this._targetArticleContainerGroups);
    await this.removeUnnecessaryArticles(this._targetArticleContainerGroups, baseRangeOnLineContainer);

    const oldProject: ProjectVO = this.projectModel.currentProject;
    this.resetCurrentProject();
    this.transferProjectData(this.projectModel.currentProject, oldProject);
    this.projectModel.currentProject.name = this.localeResourceManager.getValueByKey('rightPanel.label.new_project');
    this.projectModelService.currentProjectType = "FREE_MOUNTING";
    this.projectModel.currentProject.options = [];

    // update texture areas (split merge with removed articles and recreate texture)
    this.textureConnectionUtils.updateMergeOfTextureConnectionsInContainers(
      this._targetArticleContainerGroups.reduce((acc, i) => (acc = acc.concat(i.articleContainers)), [])
    );

    try {
      await this.miniTerminalBlocksArticleManager.quickLoadArticle({
        data: ArticleConstants.DEFAULT_FREE_MOUNTING_ARTICLE_ID,
        quickLoadType: ArticleManager.QUICK_LOAD_TYPE_LOAD_BY_ARTICLE_ID,
      });
    } catch(error) {
      // TODO SLK Is this call correct?
      this.miniTerminalBlocksProjectCreationServiceDelegator.projectCreationFailureHandler();
    }
  }

  private resetCurrentProject(): void {
    this.clearService.clearManagers();
    this.projectModelService.resetCurrentProjectToNew(false, false);
  }

  private transferProjectData(newProject: ProjectVO, oldProject: ProjectVO): void {
    const propertyValues: PropertyValueVO[] = oldProject.infoProperties.getMultiple("brokenCodingFingerPositions");

    if (!ArrayUtils.isEmpty(propertyValues)) {
      // Normally this type of property is set to MCS/GDS project types, current functionality is disabled for such projects.
      // Current functionality is introduce to support future changes.
      newProject.infoProperties.setMultiple("brokenCodingFingerPositions", propertyValues);
    }
  }

  private getBaseRangeOnLineContainer(articleContainerGroups: ArticleContainerGroup[]): ArticleContainer {
    const articleContainerGroup: ArticleContainerGroup = articleContainerGroups[0];
    return this.articleContainerService.getBaseRangeOnLineContainer(articleContainerGroup.articleContainers[0]);
  }

  /**
   * Target articles are selected article containers (complex and simple) with giving X-COM connections in
   * the X-COM or rail projects (except MCS/GDS)
   * @return
   */
  private findTargetArticleContainers(): ArticleContainerGroup[] {
    const articleContainerGroups: ArticleContainerGroup[] = [];
    let selectedArticleContainers: ArticleContainer[] = this.selectionModelService.getArticleContainers();
    selectedArticleContainers = selectedArticleContainers.filter((articleContainer: ArticleContainer) =>
      this.isXcomArticleContainer(articleContainer)
    );

    if (ArrayUtils.isEmpty(selectedArticleContainers)) {
      return [];
    }
    const processedArticleGUIDs: string[] = [];
    this.excludeForwardConnectedXcomArticles(selectedArticleContainers);
    for (const articleContainer of selectedArticleContainers) {
      if (processedArticleGUIDs.indexOf(articleContainer.articleGUID) !== -1) {
        continue;
      }
      if (this.compositeArticleInfoUtils.isCompositePartArticle(articleContainer)) {
        const baseCompositeArticleParts: ArticleContainer[] = this.containerService.getBaseCompositePartArticles(articleContainer);
        for (const compositeArticleContainer of baseCompositeArticleParts) {
          processedArticleGUIDs.push(compositeArticleContainer.articleGUID);
        }
        const usageId: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(articleContainer);
        articleContainerGroups.push(this.createArticleContainerGroup(usageId, baseCompositeArticleParts));
      } else {
        processedArticleGUIDs.push(articleContainer.articleGUID);
        articleContainerGroups.push(this.createArticleContainerGroup(articleContainer.articleGUID, [articleContainer]));
      }
    }
    this.combineArticleContainerGroups(articleContainerGroups);
    const sortedArticleContainerGroups: ArticleContainerGroup[] = this.sortArticleContainerGroups(articleContainerGroups);
    return sortedArticleContainerGroups;
  }

  /**
   * Excludes article container if it is X-COM article container that is connected to the selected X-COM article container
   * @param articleContainers
   */
  private excludeForwardConnectedXcomArticles(articleContainers: ArticleContainer[]): void {
    if (ArrayUtils.isEmpty(articleContainers) || articleContainers.length === 1) {
      return;
    }
    const selectedArticleContainers: ArticleContainer[] = articleContainers.concat();
    const articleUsageIds: string[] = [];
    for (const articleContainer of selectedArticleContainers) {
      const articleUsageId: string = articleContainer.articleGUID;
      articleUsageIds.push(articleUsageId);
    }
    for (const selectedArticleContainer of selectedArticleContainers) {
      const backwardConnectedArticleUsageIds: string[] = [];
      this.getAllBackwardConnectedArticleUsageIds(selectedArticleContainer.articleUsage, backwardConnectedArticleUsageIds);
      if (ArrayUtils.sourceArrayContainsElementFromTargetArray(backwardConnectedArticleUsageIds, articleUsageIds)) {
        articleContainers.splice(articleContainers.indexOf(selectedArticleContainer), 1);
      }
    }
  }

  private createArticleContainerGroup(groupId: string, articleContainers: ArticleContainer[]): ArticleContainerGroup {
    const articleContainerGroup: ArticleContainerGroup = new ArticleContainerGroup();
    articleContainerGroup.groupId = groupId;
    articleContainerGroup.articleContainers = articleContainers;
    return articleContainerGroup;
  }

  /**
   * Combines base X-COM articles into groups if they are connected with each other (for example by strip).
   * If base x-COM articles are rotated, than all group will be added to the new project without rotation,
   * but relative positions between grouped articles will stay unchanged:
   * Rotated:
   *
   * [->W->]                            [<-W<-]
   * A     C      will be converted to  C     A
   * ----------                         ----------
   * @param articleContainerGroups
   */
  private combineArticleContainerGroups(articleContainerGroups: ArticleContainerGroup[]): void {
    const groupIdToForwardConnectedUsageIds: Dictionary<string, string[]> = new Dictionary<string, string[]>();
    for (const group of articleContainerGroups) {
      const forwardConnectedUsageMap: Dictionary<string, ArticleUsageVO> = this.collectForwardConnectedArticleUsages(group.articleContainers);
      group.forwardConnectedUsageMap = forwardConnectedUsageMap;
      if (!forwardConnectedUsageMap.isEmpty()) {
        groupIdToForwardConnectedUsageIds.setValue(group.groupId, forwardConnectedUsageMap.keys());
      }
    }
    const processedArticleGroupIds: string[] = [];
    for (const groupId of groupIdToForwardConnectedUsageIds.keys()) {
      if (processedArticleGroupIds.indexOf(groupId) !== -1) {
        continue;
      }
      processedArticleGroupIds.push(groupId);
      const currentConnectedUsageIds: string[] = groupIdToForwardConnectedUsageIds.getValue(groupId);
      for (const testedGroupId of groupIdToForwardConnectedUsageIds.keys()) {
        if (processedArticleGroupIds.indexOf(testedGroupId) !== -1) {
          continue;
        }
        const connectedUsageIds: string[] = groupIdToForwardConnectedUsageIds.getValue(testedGroupId);
        if (ArrayUtils.sourceArrayContainsElementFromTargetArray(currentConnectedUsageIds, connectedUsageIds)) {
          this.combineTwoArticleGroups(groupId, testedGroupId, articleContainerGroups);
          processedArticleGroupIds.push(testedGroupId);
        }
      }
    }
  }

  private combineTwoArticleGroups(sourceGroupId: string, targetGroupId: string, articleContainerGroups: ArticleContainerGroup[]): void {
    const sourceGroup: ArticleContainerGroup = this.findArticleContainerGroupByGroupId(sourceGroupId, articleContainerGroups);
    const targetArticleContainers: ArticleContainer[] = sourceGroup.articleContainers;
    const targetGroup: ArticleContainerGroup = this.findArticleContainerGroupByGroupId(targetGroupId, articleContainerGroups);
    const articleContainers: ArticleContainer[] = targetGroup.articleContainers;
    ArrayUtils.mergeArrays(targetArticleContainers, articleContainers);
    articleContainerGroups.splice(articleContainerGroups.indexOf(targetGroup), 1);

    for (const usageId of targetGroup.forwardConnectedUsageMap.keys()) {
      sourceGroup.forwardConnectedUsageMap.setValue(usageId, targetGroup.forwardConnectedUsageMap.getValue(usageId));
    }
  }

  private findArticleContainerGroupByGroupId(groupId: string, groups: ArticleContainerGroup[]): ArticleContainerGroup {
    for (const group of groups) {
      if (groupId === group.groupId) {
        return group;
      }
    }
    return null;
  }

  private calculateArticleWidth(givingXcomConnections: Connection[], articleContainer: ArticleContainer): number {
    const firstGivingRectangle: Rectangle3D = this.connectionInfoService.calculateRectangleByConnection(givingXcomConnections[0], true);
    const lastGivingRectangle: Rectangle3D = this.connectionInfoService.calculateRectangleByConnection(
      givingXcomConnections[givingXcomConnections.length - 1],
      true
    );
    let articleLine: AdvancedLine;
    if (ArticleUsageUtils.isArticleRotated(articleContainer.articleUsage)) {
      articleLine = AdvancedLine.createInstanceByArticle(articleContainer.article, lastGivingRectangle.point2, firstGivingRectangle.point1);
      articleLine.reverseOverlapProperties();
    } else {
      articleLine = AdvancedLine.createInstanceByArticle(articleContainer.article, firstGivingRectangle.point1, lastGivingRectangle.point2);
    }
    return articleLine.length;
  }

  /**
   * Article containers are sorted by the middle point of the most left giving X-COM connection.
   * @param articleContainerGroups
   * @return
   */
  private sortArticleContainerGroups(articleContainerGroups: ArticleContainerGroup[]): ArticleContainerGroup[] {
    const usageIdToGroupIdMapping: Dictionary<string, string> = new Dictionary<string, string>();
    const articlePositions: { usageId: string; position: Vector3 }[] = [];
    for (const group of articleContainerGroups) {
      let articleContainers: ArticleContainer[] = group.articleContainers;
      articleContainers = ArticleContainerUtils.sortArticleContainersByDistanceOfPosition(
        articleContainers,
        CreateNewProjectWithPresetArticlesManager.POINT_TO_SORT_BY_X
      );
      group.articleContainers = articleContainers;
      const articleContainer: ArticleContainer = articleContainers[0];
      usageIdToGroupIdMapping.setValue(articleContainer.articleGUID, group.groupId);
      const givingConnection: Connection = this.findFirstXcomGivingConnection(articleContainer);
      const middlePoint: Vector3 = MathUtils.calculateMiddlePointByRectangle(givingConnection.compositionRectangle);
      articlePositions.push({ usageId: articleContainer.articleGUID, position: middlePoint });
    }

    articlePositions.sort((positionMapping1: { usageId: string; position: Vector3 }, positionMapping2: { usageId: string; position: Vector3 }) => {
      const position1: Vector3 = positionMapping1.position;
      const position2: Vector3 = positionMapping2.position;
      if (MathUtils.equalsNumber(position1.x, position2.x)) {
        return MathUtils.compare(position2.z, position1.z, 0);
      } else {
        return MathUtils.compare(position2.x, position1.x, 0);
      }
    });

    const sortedArticleContainerGroups: ArticleContainerGroup[] = [];
    for (const articlePositionMapping of articlePositions) {
      const articleGuid: string = articlePositionMapping.usageId;
      const articleGroup: string = usageIdToGroupIdMapping.getValue(articleGuid);
      sortedArticleContainerGroups.push(this.findArticleContainerGroupByGroupId(articleGroup, articleContainerGroups));
    }
    return sortedArticleContainerGroups;
  }

  /**
   * Article containers that are transferred to the new project must be detached from the parent connected articles,
   * that will be removed.
   * @param articleContainerGroups
   */
  private async detachTargetArticleContainers(articleContainerGroups: ArticleContainerGroup[]): Promise<void> {
    await this.detachWireBridgeArticleContainers(articleContainerGroups);
    for (const articleContainerGroup of articleContainerGroups) {
      for (const articleContainer of articleContainerGroup.articleContainers) {
        this.detachArticleContainerFromParent(articleContainer);
      }
    }
  }

  /**
   * Wire bridges are not transferred to the new project.
   * @param articleContainerGroups
   */
  private async detachWireBridgeArticleContainers(articleContainerGroups: ArticleContainerGroup[]): Promise<void> {
    const wireBridgeUsageIds: string[] = [];
    for (const articleContainerGroup of articleContainerGroups) {
      const articleUsages: ArticleUsageVO[] = articleContainerGroup.forwardConnectedUsageMap.values();
      for (const articleUsage of articleUsages) {
        const article: ArticleVO = this.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
        if (ArticleUtils.isWireBridgeArticle(article)) {
          this.addNotTransferredArticleInformation(article.articleNo);
          wireBridgeUsageIds.push(articleUsage.id);
          articleContainerGroup.forwardConnectedUsageMap.remove(articleUsage.id);
        }
      }
    }
    await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(wireBridgeUsageIds);
  }

  private addNotTransferredArticleInformation(articleNo: string): void {
    if (!this._notTransferredArticleMap.containsKey(articleNo)) {
      this._notTransferredArticleMap.setValue(articleNo, 0);
    }
    this._notTransferredArticleMap.setValue(articleNo, this._notTransferredArticleMap.getValue(articleNo) + 1);
  }

  private detachArticleContainerFromParent(articleContainer: ArticleContainer): void {
    const backwardConnectedArticleUsageIds: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleContainer.articleUsage);
    const givingConnectionIdentities: MetaElementUsageIdentityVO[] = articleContainer.getCurrentArticleGivingAreaIds();
    for (const givingConnectionIdentity of givingConnectionIdentities) {
      const connectionAreaUsage: ConnectionAreaUsageVO = articleContainer.findMetaElementUsageByName(
        givingConnectionIdentity.metaElementName
      ) as ConnectionAreaUsageVO;
      if (!connectionAreaUsage || !connectionAreaUsage.connection) {
        continue;
      }
      const connection: ConnectionVO = connectionAreaUsage.connection;
      const backwardConnectedUsageId: string = connection.acceptingAreaId.articleUsageId;
      if (backwardConnectedArticleUsageIds.indexOf(backwardConnectedUsageId) === -1) {
        continue;
      }
      articleContainer.detachMetaElementUsageByName(givingConnectionIdentity.metaElementName);
    }
  }

  /**
   * Removes articles that will not be transferred to the new project, at this moment target article containers are
   * detached from removed article containers and will remain in the composition. If current project has mounting rail than
   * it will be removed from the project.
   * @param targetArticleGroups
   * @param baseRangeOnLineContainer
   */
  private async removeUnnecessaryArticles(targetArticleGroups: ArticleContainerGroup[], baseRangeOnLineContainer: ArticleContainer): Promise<void> {
    const articleUsageIdsToExclude: string[] = [];
    for (const articleContainerGroup of targetArticleGroups) {
      this.getExcludedUsageIds(articleContainerGroup, articleUsageIdsToExclude);
    }
    const articleUsageIdsToDelete: string[] = [];
    const articleContainers: ArticleContainer[] = this.containerService.enumerateArticleContainersInGlobalExcept(articleUsageIdsToExclude);
    for (const articleContainer of articleContainers) {
      articleUsageIdsToDelete.push(articleContainer.articleGUID);
    }
    await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(articleUsageIdsToDelete);

    if (baseRangeOnLineContainer) {
      const articleUsage: ArticleUsageVO = baseRangeOnLineContainer.articleUsage;
      ProjectUtils.removeArticleUsage(this.projectModel.currentProject, articleUsage);
    }
  }

  private getExcludedUsageIds(articleContainerGroup: ArticleContainerGroup, articleUsageIdsToExclude: string[]): void {
    for (const targetArticle of articleContainerGroup.articleContainers) {
      if (ArticleUtils.isCompositeArticle(targetArticle.article)) {
        const compositeArticleParts: ArticleContainer[] = this.containerService.getCompositePartArticles(targetArticle);
        for (const compositePart of compositeArticleParts) {
          this.addUsageId(compositePart.articleGUID, articleUsageIdsToExclude);
        }
      } else {
        this.addUsageId(targetArticle.articleGUID, articleUsageIdsToExclude);
      }
      const forwardConnectedArticleIds: string[] = this.getAllForwardConnectedUsageIds(targetArticle.articleUsage);
      for (const usageId of forwardConnectedArticleIds) {
        this.addUsageId(usageId, articleUsageIdsToExclude);
      }
    }
  }

  private addUsageId(usageId: string, usageIds: string[]): void {
    if (usageIds.indexOf(usageId) === -1) {
      usageIds.push(usageId);
    }
  }

  private isXcomArticleContainer(articleContainer: ArticleContainer): boolean {
    return this.findXcomGivingConnections([articleContainer]).length > 0;
  }

  public async mountingRailAttached(): Promise<void> {
    if (!this._isActive) {
      return;
    }
    const articleUsages: ArticleUsageVO[] = this.projectArticleService.findArticleUsagesByType(ArticleTypeConstants.TYPE_MOUNTING_RAIL);
    let railContainer: ArticleContainer;
    if (!ArrayUtils.isEmpty(articleUsages)) {
      railContainer = this.containerService.findArticleContainerInGlobal((articleUsages[0] as ArticleUsageVO).id);
    }
    if (railContainer) {
      await this.attachTargetArticles(railContainer);
      this.projectContentPresenter.init();
      this.handleSuccess();
    } else {
      await this.handleError(CreateNewProjectWithPresetArticlesManager.ERROR_MESSAGE);
    }
  }

  private async attachTargetArticles(railArticleContainer: ArticleContainer): Promise<void> {
    const article: ArticleVO = railArticleContainer.article;
    const rangeOnLineConnection: ConnectionAreaVO = ConnectionInfoUtils.findDefaultRangeOnLineConnection(article.metaElements);
    const metaElementUsage: MetaElementUsageVO = this.projectMetaElementService.addMetaElementUsage(railArticleContainer.articleGUID, rangeOnLineConnection);

    this._insertArticleHelper = this.insertArticleHelperFactory.createInstanceByAcceptingIdentity(metaElementUsage.identity);
    for (const targetArticleGroup of this._targetArticleContainerGroups) {
      await this.insertArticleContainerOnRail(targetArticleGroup, railArticleContainer, rangeOnLineConnection);
    }
  }

  private async insertArticleContainerOnRail(articleGroup: ArticleContainerGroup, railArticleContainer: ArticleContainer, rangeOnLineConnection: ConnectionAreaVO): Promise<void> {
    let articleContainers: ArticleContainer[] = articleGroup.articleContainers;
    const givingConnections: Connection[] = this.findXcomGivingConnections(articleContainers);
    if (ArrayUtils.isNotEmpty(givingConnections)) {
      this.updateRotationAngle(articleGroup.articleContainers);
      const baseArticleContainer: ArticleContainer = articleContainers[0] as ArticleContainer;
      const insertProcessor: IArticleInsertProcessor = this.insertProcessorRegistry.get(baseArticleContainer.article);
      const forwardConnectedArticleUsageIds: string[] = articleGroup.forwardConnectedUsageMap.keys();
      const forwardConnectedArticleContainers: ArticleContainer[] = this.getArticleContainers(forwardConnectedArticleUsageIds);
      articleContainers = articleContainers.concat(forwardConnectedArticleContainers);
      const articleGUIDs: string[] = [];
      for (const articleContainer of articleContainers) {
        articleGUIDs.push(articleContainer.articleGUID);
        CarrierBlockUtils.removeCarrierBlockGuidAndBmk(articleContainer.articleUsage);
      }
      this.articleLineHolder.clearArticleLineCacheByGUIDs(articleGUIDs);
      const width: number = this.calculateArticleWidth(givingConnections, baseArticleContainer);
      const line: AdvancedLine = this.calculateArticleLine(articleGroup, width);
      const acceptingConnection: Connection = this._insertArticleHelper.createConnectionOnLine(line, rangeOnLineConnection);
      // Composition already have article usages of article containers that will be inserted.
      // Duplicated article containers will be created if standard messages will be dispatched.
      const isMessageDispatchRequired: boolean = false;
      insertProcessor.process(
        articleContainers,
        articleGroup.articleContainers,
        line,
        acceptingConnection,
        givingConnections,
        0,
        undefined,
        isMessageDispatchRequired
      );
      acceptingConnection.articleContainer.disposeConnectionByName(acceptingConnection.id);
      this.updateProcessedArticleLine(articleGroup.articleContainers);
      this.checkInsertedArticleContainers(articleGroup);
    } else {
      await this.handleError(CreateNewProjectWithPresetArticlesManager.ERROR_MESSAGE);
    }
  }

  private collectForwardConnectedArticleUsages(articleContainers: ArticleContainer[]): Dictionary<string, ArticleUsageVO> {
    const forwardConnectedArticleUsageMap: Dictionary<string, ArticleUsageVO> = new Dictionary<string, ArticleUsageVO>();
    for (const articleContainer of articleContainers) {
      this.getAllForwardConnectedUsageIdsMap(articleContainer.articleUsage, forwardConnectedArticleUsageMap);
    }
    return forwardConnectedArticleUsageMap;
  }

  private getAllForwardConnectedUsageIds(articleUsage: ArticleUsageVO): string[] {
    const forwardConnectedArticleUsageIdsMap: Dictionary<string, ArticleUsageVO> = new Dictionary<string, ArticleUsageVO>();
    const forwardConnectedArticleUSageIds: string[] = [];
    this.getAllForwardConnectedUsageIdsMap(articleUsage, forwardConnectedArticleUsageIdsMap);
    for (const forwardArticleUsage of forwardConnectedArticleUsageIdsMap.values()) {
      forwardConnectedArticleUSageIds.push(forwardArticleUsage.id);
    }
    return forwardConnectedArticleUSageIds;
  }

  private getAllForwardConnectedUsageIdsMap(articleUsage: ArticleUsageVO, forwardConnectedArticleUsageMap: Dictionary<string, ArticleUsageVO>): void {
    const forwardConnectedUsageIds: string[] = ArticleUsageUtils.getAcceptingAreaIds(articleUsage);
    for (const usageId of forwardConnectedUsageIds) {
      const forwardConnectedArticleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(usageId);
      if (!forwardConnectedArticleUsageMap.containsKey(usageId)) {
        forwardConnectedArticleUsageMap.setValue(usageId, forwardConnectedArticleUsage);
      }
      this.getAllForwardConnectedUsageIdsMap(forwardConnectedArticleUsage, forwardConnectedArticleUsageMap);
    }
  }

  private getAllBackwardConnectedArticleUsageIds(articleUsage: ArticleUsageVO, articleUsageIds: string[]): void {
    const backwardConnectedArticleUsageIds: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleUsage);
    for (const articleUsageId of backwardConnectedArticleUsageIds) {
      const backwardConnectedArticleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(articleUsageId);
      if (articleUsageIds.indexOf(backwardConnectedArticleUsage.id) === -1) {
        articleUsageIds.push(backwardConnectedArticleUsage.id);
      }
      this.getAllBackwardConnectedArticleUsageIds(backwardConnectedArticleUsage, articleUsageIds);
    }
  }

  private getArticleContainers(usageIds: string[]): ArticleContainer[] {
    return this.containerService.findArticleContainersInGlobalByGuids(usageIds);
  }

  private findXcomGivingConnections(articleContainerGroup: ArticleContainer[]): Connection[] {
    const givingConnections: Connection[] = [];
    for (const articleContainer of articleContainerGroup) {
      const givingConnection: Connection = this.findFirstXcomGivingConnection(articleContainer);
      if (givingConnection) {
        givingConnections.push(givingConnection);
      }
    }
    return ConnectionInfoUtils.sortConnections(givingConnections, true);
  }

  private updateRotationAngle(articleContainers: ArticleContainer[]): void {
    for (const articleContainer of articleContainers) {
      ArticleUsageUtils.updateAppliedRotationAngle(articleContainer.articleUsage, 0);
    }
  }

  private findFirstXcomGivingConnection(articleContainer: ArticleContainer): Connection {
    const givingConnectionAreas: ConnectionAreaVO[] = ArticleUtils.getGivingXcomConnectionAreas(articleContainer.article);
    const firstConnectionArea: ConnectionAreaVO = givingConnectionAreas[0];
    if (!firstConnectionArea) {
      return null;
    } else {
      return articleContainer.findFirstConnectionByName(firstConnectionArea.name);
    }
  }

  /**
   * Article line is calculated taking into account a gap between article containers.
   * @param articleContainerGroup
   * @param width
   * @return
   */
  private calculateArticleLine(articleContainerGroup: ArticleContainerGroup, width: number): AdvancedLine {
    const baseLine: AdvancedLine = this._insertArticleHelper.baseLine;
    let startPoint: Vector3;
    if (!this._processedArticleLine) {
      startPoint = baseLine.start.clone();
    } else {
      startPoint = this._processedArticleLine.end.sub(CreateNewProjectWithPresetArticlesManager.ARTICLE_OFFSET);
    }
    const endPoint: Vector3 = startPoint.clone().sub(new Vector3(width));
    const firstArticleContainer: ArticleContainer = articleContainerGroup.articleContainers[0];
    return AdvancedLine.createInstanceByArticle(firstArticleContainer.article, startPoint, endPoint);
  }

  private updateProcessedArticleLine(articleContainerGroup: ArticleContainer[]): void {
    const commonLine: AdvancedLine = this._insertArticleHelper.calculateCommonArticlesLine(articleContainerGroup);
    this._processedArticleLine = commonLine;
  }

  /**
   * Checks if all target and forward connected article containers are successfuly transferred to the new project.
   * Some article containers can be removed during insert process, for example if two or more X-COM articles
   * with connected adapters have common strip with, than such strip will be removed.
   * @param articleContainerGroup
   */
  private checkInsertedArticleContainers(articleContainerGroup: ArticleContainerGroup): void {
    const notTransferredUsageIds: string[] = [];
    const articleUsages: ArticleUsageVO[] = articleContainerGroup.forwardConnectedUsageMap.values();
    for (const articleContainer of articleContainerGroup.articleContainers) {
      articleUsages.push(articleContainer.articleUsage);
    }
    for (const articleUsage of articleUsages) {
      if (notTransferredUsageIds.indexOf(articleUsage.id) !== -1) {
        continue;
      }
      const articleContainerInGlobal: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsage.id);
      if (articleContainerInGlobal) {
        continue;
      }
      const article: ArticleVO = this.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
      if (ArticleUtils.isCompositeArticle(article)) {
        const compositeArticle: ArticleVO = this.compositeArticleInfoUtils.getCompositeArticleFromUsage(articleUsage);
        this.addNotTransferredArticleInformation(compositeArticle.articleNo);
        const compositePartUsageIds: string[] = this.getArticleUsageIdsWithSameCompositeUsageId(articleUsage, articleUsages);
        ArrayUtils.mergeArrays(notTransferredUsageIds, compositePartUsageIds);
      } else {
        notTransferredUsageIds.push(articleUsage.id);
        this.addNotTransferredArticleInformation(article.articleNo);
      }
    }
  }

  /**
   * Returns article usages with the equal composite article usage id from provided source array.
   * @param articleUsage
   * @param articleUsages
   * @return
   */
  private getArticleUsageIdsWithSameCompositeUsageId(articleUsage: ArticleUsageVO, articleUsages: ArticleUsageVO[]): string[] {
    const compositeArticleUsageId: string = ArticleUsageUtils.getCompositeArticleUsageIdFromUsage(articleUsage);
    const compositeArticleUsageIds: string[] = [];
    for (const usage of articleUsages) {
      const compArticleUsage: string = ArticleUsageUtils.getCompositeArticleUsageIdFromUsage(usage);
      if (compArticleUsage === compositeArticleUsageId) {
        compositeArticleUsageIds.push(usage.id);
      }
    }
    return compositeArticleUsageIds;
  }
}
